/**
 * The colours a growth stage is painted: the colour the server sends for it
 * (its color_key, applied for this gardener) once the stages have been read
 * (registerStageColors), else a neutral gray until they arrive. The app holds
 * no colour of its own for any stage (batch 13f, F3): the database decides.
 *
 * Used by: StagesContext (registerStageColors, during render) and the plant
 * row, tile and Seasons page (getStageTheme).
 *
 * @module theme/stageThemes
 */
import { StageId, Stage } from '../types/stage';
import { themeForColorKey, isColorKey, PaletteEntry } from './colorPalette';

/** The palette colour drawn for a stage until the stages are read, and for one the server did not send. */
const NEUTRAL_STAGE_COLOR = 'gray';

/**
 * The colour each stage is currently painted, as resolved for this gardener.
 *
 * Stage colours became something a person chooses, which means they live in
 * the database rather than in this file. Eight call sites ask for a theme with
 * nothing but an id in hand, so rather than thread a Stage object through all
 * of them, StagesProvider registers what it fetched and this map answers.
 *
 * Module state, deliberately: there is exactly one signed-in gardener per
 * running app, the map is written only from the provider, and a reader that
 * arrives before the fetch lands simply gets the neutral colour and is
 * re-rendered when the provider's query resolves.
 */
const resolvedStageColors = new Map<StageId, string>();

/**
 * Records the colours a set of stages should be painted.
 *
 * Called by StagesProvider whenever stages are fetched or re-fetched, which
 * includes straight after a gardener changes one.
 *
 * @param stages - Stages as the server resolved them, overrides applied.
 */
export function registerStageColors(stages: Stage[] | undefined): void {
    if (!stages) return;
    resolvedStageColors.clear();
    for (const stage of stages) {
        if (isColorKey(stage.color_key)) {
            resolvedStageColors.set(stage.stage_id, stage.color_key);
        }
    }
}

/**
 * The palette entry a stage should be drawn with.
 *
 * The colour the server sent for the stage; before the stages are read, or for
 * a stage the server did not send, a neutral gray (the badge is drawn again
 * when the provider's query resolves).
 *
 * @param stageId - The stage.
 * @returns Its palette entry.
 */
export function getStageTheme(stageId: StageId): PaletteEntry {
    const chosen = resolvedStageColors.get(stageId);
    return themeForColorKey(chosen ?? NEUTRAL_STAGE_COLOR);
}
